import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { distinctUntilChanged, map, shareReplay, switchMap } from 'rxjs';

import { CurrentUserSelectors, UserSelectors } from '@core/account-data';
import type { User } from '@core/account-types';
import { filterBoolean, filterDefined } from '@core/common-legacy';
import type { OfferItem } from '@core/offers-types';
import { defineTableColumn } from '@core/user-interface-legacy';
import { deepEqual } from '@core/utils-legacy';

import type { RequisitionAccessConfig } from '../../../offers-page/offers-page.types';

export const defineOfferTableColumn = defineTableColumn<OfferItem>;

export const injectUsersMap = (store = inject(Store), destroyRef = inject(DestroyRef)) =>
  store.select(UserSelectors.entities()).pipe(
    filterBoolean,
    map(users =>
      users.reduce(
        (acc, user) => ({
          ...acc,
          [user.id]: user,
        }),
        <Record<number, User>>{},
      ),
    ),
    takeUntilDestroyed(destroyRef),
    shareReplay(1),
  );

export const requisitionAccessConfig$ = (store = inject(Store), destroyRef = inject(DestroyRef)) =>
  store.select(CurrentUserSelectors.data).pipe(
    filterDefined,
    switchMap(currentUser =>
      injectUsersMap(store, destroyRef).pipe(map(usersMap => usersMap[currentUser.id])),
    ),
    map(
      (user): Required<RequisitionAccessConfig> => ({
        hasAccessToAllRequisitions: !!user.viewAllRequisitions,
        hasAccessToRequisitionIds: user.requisitionIds || [],
      }),
    ),
    distinctUntilChanged((a, b) => deepEqual(a, b)),
    takeUntilDestroyed(destroyRef),
    shareReplay(1),
  );
